import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useForm, FormProvider } from 'react-hook-form';
import { TextFieldController } from '../../../components/organisms/form/controllers/TextFieldController';
import { BasicTableHead, BasicTableHeadProps } from '../../../components/molecules';
import { MenuHeaderTemplate } from '../../../components/templates/MenuHeaderTemplate';
import { KeyboardDatePickerController } from '../../../components/organisms/form/controllers/KeyboardDatePickerController';
import { useCouponPackUsageStatus } from '../hooks/useCouponPackUsageStatus';
import { ReadMoreButton } from '../../../components/organisms/general/ReadMoreButton';
import { Search } from '@material-ui/icons';
import { formatJstDate, validateStartEnd } from '../../../util';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles((theme: Theme) => ({
  searchForm: {
    marginTop: theme.spacing(1),
  },
  searchButton: {
    textAlign: 'center',
  },
  resultList: {
    marginTop: theme.spacing(3),
  },
  groupButton: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonSearch: {
    width: '232px',
    marginTop: '0px',
  },
  buttonExport: {
    width: '232px',
    marginLeft: '44px',
    marginTop: '0px',
  },
}));

export type SearchFormValues = {
  startDate: Date | null;
  endDate: Date | null;
  couponPackCode: string;
  couponPackName: string;
};

export const CouponPackUsageStatusListPage: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const props = useCouponPackUsageStatus();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const now = DateTime.now();
  const searchForm = useForm<SearchFormValues>({
    defaultValues: {
      startDate: now.startOf('month').toJSDate(),
      endDate: now.endOf('month').toJSDate(),
      couponPackCode: '',
      couponPackName: '',
    },
  });
  const [datePickerRange, setDatePickerRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: now.startOf('month').toJSDate(),
    endDate: now.endOf('month').toJSDate(),
  });

  const { handleSubmit, watch } = searchForm;

  const tableHeadProps: BasicTableHeadProps = {
    columns: [
      {
        name: 'couponPackCode',
        label: '回数券コード',
        minWidth: theme.spacing(20),
        align: 'left',
      },
      {
        name: 'couponPackName',
        label: '回数券名',
        minWidth: theme.spacing(20),
        align: 'left',
      },
      {
        name: 'couponCode',
        label: 'クーポンコード',
        minWidth: theme.spacing(20),
        align: 'left',
      },
      {
        name: 'couponName',
        label: 'クーポン名',
        minWidth: theme.spacing(20),
        align: 'left',
      },
      {
        name: 'issuedPackCount',
        label: '回数券発行数',
        minWidth: theme.spacing(15),
        align: 'right',
      },
      {
        name: 'issuedCouponCount',
        label: 'クーポン発行数',
        minWidth: theme.spacing(17),
        align: 'right',
      },
      {
        name: 'usedCouponCount',
        label: 'クーポン消込数',
        minWidth: theme.spacing(17),
        align: 'right',
      },
      {
        name: 'action',
        label: '',
        minWidth: theme.spacing(5),
        align: 'center',
      },
    ],
  };

  const validateRangeDatePicker = (data: SearchFormValues): boolean => {
    const startDate = new Date(data.startDate!);
    const endDate = new Date(data.endDate!);

    const startDateTime = DateTime.fromJSDate(startDate);
    const endDateTime = DateTime.fromJSDate(endDate);

    if (!startDateTime.isValid || !endDateTime.isValid) {
      const key = enqueueSnackbar('表示期間、有効期限、取得期限の日時情報を正しく入力してください', {
        variant: 'warning',
        autoHideDuration: 5000,
        onClick: () => closeSnackbar(key),
      });
      return false;
    }

    if (data.startDate && data.endDate && !validateStartEnd(startDateTime, endDateTime, true)) {
      const key = enqueueSnackbar('開始日時が前になるように修正してください', {
        variant: 'warning',
        persist: true,
        onClick: () => closeSnackbar(key),
      });
      return false;
    }

    return true;
  };

  const onSearch = (data: SearchFormValues): void => {
    if (!validateRangeDatePicker(data)) return;
    props.onSearch(data);
    setDatePickerRange({
      startDate: data.startDate,
      endDate: data.endDate,
    });
  };

  const onCsvOut = (data: SearchFormValues): void => {
    if (!validateRangeDatePicker(data)) return;
    props.onCsvOut(data);
  };

  useEffect(() => {
    // init search
    props.onSearch(searchForm.getValues());
  }, []);

  return (
    <MenuHeaderTemplate
      header={{
        title: '回数券利用状況集計',
        loading: undefined,
        progressLabel: undefined,
      }}
    >
      <Box px={4}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormProvider {...searchForm}>
              <form onSubmit={handleSubmit(onSearch)} autoComplete="off" noValidate>
                <Grid container spacing={2}>
                  <Grid container item xs={12} spacing={2} alignItems="center">
                    <Grid item>
                      <KeyboardDatePickerController
                        required={false}
                        name="startDate"
                        label="利用日時"
                        maxDate={
                          watch('endDate') &&
                          new Date(watch('endDate')!) instanceof Date &&
                          !isNaN(new Date(watch('endDate')!).getTime())
                            ? formatJstDate(new Date(watch('endDate')!).toISOString())
                            : undefined
                        }
                      />
                    </Grid>
                    <Grid item>
                      <Box sx={{ fontSize: '2rem', height: '24px' }}>-</Box>
                    </Grid>
                    <Grid item>
                      <KeyboardDatePickerController
                        required={false}
                        name="endDate"
                        label="利用日時"
                        minDate={
                          watch('startDate') &&
                          new Date(watch('startDate')!) instanceof Date &&
                          !isNaN(new Date(watch('startDate')!).getTime())
                            ? formatJstDate(new Date(watch('startDate')!).toISOString())
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid container item xs={12} spacing={2}>
                    <Grid item>
                      <Box className={classes.groupButton}>
                        <TextFieldController
                          name="couponPackCode"
                          label="回数券コード"
                          size="small"
                          className={classes.buttonSearch}
                          rule={{
                            maxLength: {
                              value: 30,
                              message: '30文字以内で入力してください',
                            },
                          }}
                        />
                        <TextFieldController
                          name="couponPackName"
                          label="回数券名"
                          size="small"
                          className={classes.buttonExport}
                          rule={{
                            maxLength: {
                              value: 30,
                              message: '30文字以内で入力してください',
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid container item xs={12} spacing={2}>
                    <Grid item>
                      <Box className={classes.groupButton}>
                        <Button
                          type="submit"
                          variant={'outlined'}
                          size={'medium'}
                          color={'default'}
                          fullWidth
                          className={classes.buttonSearch}
                          disabled={props.loading || props.csvLoading}
                          endIcon={<Search />}
                        >
                          検索
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          size="medium"
                          color={'secondary'}
                          fullWidth
                          className={classes.buttonExport}
                          onClick={handleSubmit(onCsvOut)}
                          disabled={props.loading || props.csvLoading}
                        >
                          {props.csvLoading ? <CircularProgress size={26} /> : 'CSV出力'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </Grid>

          <Grid item xs={12} className={classes.resultList}>
            <TableContainer component={Paper}>
              {props.loading ? <LinearProgress /> : <React.Fragment />}
              <Table>
                <BasicTableHead columns={tableHeadProps.columns} />
                <TableBody>
                  {(() => {
                    if (!props.allAcquired && !props.data?.nodes) {
                      return;
                    } else if (!props.data?.nodes || props.data?.nodes.length === 0) {
                      return (
                        <TableRow>
                          <TableCell>データがありません</TableCell>
                        </TableRow>
                      );
                    } else {
                      return props.data.nodes.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponPackCode}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponPackName}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponCode}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponName}
                          </TableCell>
                          <TableCell component="th" scope="row" align="right">
                            {record.issuedPackCount}
                          </TableCell>
                          <TableCell component="th" scope="row" align="right">
                            {record.issuedCouponCount}
                          </TableCell>
                          <TableCell component="th" scope="row" align="right">
                            {record.usedCouponCount}
                          </TableCell>
                          <TableCell align="center" scope="row">
                            <Button
                              onClick={() => {
                                const query = new URLSearchParams(history.location.search);
                                query.set(
                                  'startDate',
                                  datePickerRange.startDate
                                    ? formatJstDate(new Date(datePickerRange.startDate!).toISOString())
                                    : ''
                                );
                                query.set(
                                  'endDate',
                                  datePickerRange.endDate
                                    ? formatJstDate(new Date(datePickerRange.endDate!).toISOString())
                                    : ''
                                );
                                query.set('couponPackCode', record?.couponPackCode || '');
                                query.set('couponPackName', record?.couponPackName || '');
                                query.set('couponCode', record?.couponCode || '');
                                query.set('couponName', record?.couponName || '');

                                history.push({
                                  pathname: `/coupon-pack-usage-status/${record.couponPackMasterId}`,
                                  search: query.toString(),
                                });
                              }}
                              type="button"
                              size="small"
                              color="primary"
                            >
                              明細
                            </Button>
                          </TableCell>
                        </TableRow>
                      ));
                    }
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {props.data?.nodes?.length && !props.allAcquired ? (
            <ReadMoreButton
              fetchMore={() => {
                props.onFetchMore(searchForm.getValues());
              }}
              loading={props.loading}
            />
          ) : (
            <></>
          )}
        </Grid>
      </Box>
    </MenuHeaderTemplate>
  );
};
